// Adapted from https://github.com/vercel/commerce/blob/main/site/components/product/ProductSlider/ProductSlider.tsx
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import { useState } from "react";
import ArrowLeft from "@/components/icons/arrow_left";
import ArrowRight from "@/components/icons/arrow_right";

import photoOffsiteDinner from "@/img/jobs/offsite-dinner.webp";
import photoOffsiteCampfire from "@/img/jobs/2022-campfire.webp";
import photo2022Team from "@/img/jobs/2022-team.webp";
import photoNails from "@/img/jobs/avi-marissa-nails.jpg";
import photoMeasurement from "@/img/jobs/measurement-lunch.jpg";
import photoIceCream from "@/img/jobs/ice-cream.jpg";
import photoMtTam from "@/img/jobs/mt-tam-hike.jpg";
import photoNewLunch from "@/img/jobs/new-hire-lunch.jpg";
import photoNycBrunch from "@/img/jobs/nyc-brunch.jpg";
import photoDogs from "@/img/jobs/carousel-photo-6.jpg";
import photo2021Team from "@/img/jobs/carousel-photo-9.jpg";
import photoKayakers from "@/img/jobs/carousel-photo-10.jpg";

const images = [
  {
    src: photoOffsiteDinner,
    alt: "Eating dinner at our summer 2022 offsite",
  },
  {
    src: photo2022Team,
    alt: "Team photo at our summer 2022 offsite",
  },
  {
    src: photoOffsiteCampfire,
    alt: "Gathering for a campfire at the offsite",
  },
  {
    src: photoMeasurement,
    alt: "Measurement team members meeting for dinner",
  },
  {
    src: photoNails,
    alt: "Stunning nails in full Watershed style",
  },
  {
    src: photoNewLunch,
    alt: "Recent team lunch outside our SF office",
  },
  {
    src: photoNycBrunch,
    alt: "NYC team meeting for brunch",
  },
  {
    src: photoIceCream,
    alt: "Weekend outing for ice cream in the Dogpatch",
  },
  {
    src: photo2021Team,
    alt: "Team photo at our fall 2021 offsite",
  },
  {
    src: photoKayakers,
    alt: "A few of the kayakers at the offsite",
  },
  {
    src: photoMtTam,
    alt: "Hike up Mt. Tam leading up to a pancake breakfast",
  },
  {
    src: photoDogs,
    alt: "Dogs of Watershed",
  },
];

const ImageGallery = () => {
  const [isMounted, setIsMounted] = useState(true);

  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    defaultAnimation: { duration: 1000 },
    slides: { perView: 1 },
    created: () => {
      setIsMounted(true);
      // @lachlanjc: This is a bit hacky, but makes the slider check the size of
      // the grid container so the slides are sized correctly
      setTimeout(() => {
        slider.current?.update();
      }, 250);
    },
  });

  return (
    <section className="gallery">
      <div
        ref={ref}
        className="keen-slider"
        style={{ opacity: isMounted ? 1 : 0 }}
      >
        {images.map((img) => (
          <figure key={img.alt} className="keen-slider__slide">
            <Image src={img.src} alt={img.alt} placeholder="blur" />
            <figcaption className="caption">{img.alt}</figcaption>
          </figure>
        ))}
      </div>
      <button onClick={() => slider.current.prev()} aria-label="Previous image">
        <ArrowLeft aria-hidden />
      </button>
      <button onClick={() => slider.current.next()} aria-label="Next image">
        <ArrowRight aria-hidden />
      </button>
      <style jsx>{`
        .gallery {
          position: relative;
          width: 100%;
          max-width: 100%;
          border-radius: 16px;
          overflow: hidden;
          aspect-ratio: 409 / 238;
          z-index: 1;
        }
        .keen-slider {
          display: flex;
          border-radius: 16px;
          transition: opacity 0.125s ease-in-out;
        }
        .keen-slider__slide {
          display: flex;
          align-items: flex-end;
          flex-shrink: 0;
          min-width: 100% !important;
          max-height: 100%;
          border-radius: 16px;
        }
        .keen-slider__slide img {
          border-radius: 16px;
          object-fit: cover;
          object-position: center;
          height: 100%;
        }
        .caption {
          width: 100%;
          padding: 12px 48px;
          text-align: center;
          background-color: rgba(0, 0, 0, 0.625);
          color: #fff;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          margin: 0;
          line-height: 1;
          border-radius: 0 0 16px 16px;
        }
        button {
          -webkit-appearance: none;
          border: 0;
          background-color: transparent;
          border-radius: 99999px;
          cursor: pointer;
          padding: 10px;
          width: 32px;
          height: 32px;
          position: absolute;
          bottom: 4px;
        }
        button:focus {
          outline: none;
          background-color: rgba(255, 255, 255, 0.75);
        }
        button:first-of-type {
          left: 6px;
        }
        button:last-of-type {
          right: 6px;
        }
        @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
          .caption,
          button {
            background-color: rgba(255, 255, 255, 0.25);
            -webkit-backdrop-filter: saturate(180%) blur(10px);
            backdrop-filter: saturate(180%) blur(10px);
            color: #fff;
          }
        }
      `}</style>
    </section>
  );
};

export default ImageGallery;
